import { ReactNode, useMemo, useState } from "react"
import { Household } from "@/schema"
import { InlineSelect } from "@ui/forms/inline"
import { type LucideIcon } from "lucide-react"

import { useFetchHouseholds } from "@/hooks/api/households"

import { InputOption } from "../props"

export interface InlineHouseholdSelectProps {
  label?: ReactNode
  LabelIcon?: LucideIcon
  selectedHousehold?: Pick<Household, "id" | "name"> | null
  setSelectedHouseholdId: (id: string | null) => void
  onSave?: (id: string | null) => void | Promise<void>
  isDisabled?: boolean
  fixedLabelWidth?: string
  householdType?: "Prospect" | "Client" | "prospect" | "client" | null
  companyWide?: boolean
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
  placeholderContent?: ReactNode
  renderValue?: (option: InputOption | null) => ReactNode
}

export function InlineHouseholdSelect({
  label,
  LabelIcon,
  selectedHousehold,
  setSelectedHouseholdId,
  onSave,
  isDisabled,
  fixedLabelWidth,
  householdType,
  companyWide,
  hideLabel,
  size,
  placeholderContent,
  renderValue,
}: InlineHouseholdSelectProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const { data: households, isLoading } = useFetchHouseholds(
    searchQuery,
    householdType,
    undefined,
    companyWide ? "company" : undefined
  )

  const options = useMemo(() => {
    const selectedOption =
      selectedHousehold && selectedHousehold.id
        ? {
            value: selectedHousehold.id,
            label: selectedHousehold.name,
          }
        : null
    const otherOptions =
      households?.households
        ?.filter((household) => household.id !== selectedHousehold?.id)
        .map((household) => ({
          value: household.id,
          label: household.name,
        })) || []

    return selectedOption ? [selectedOption, ...otherOptions] : otherOptions
  }, [households?.households, selectedHousehold])

  const handleValueChange = async (value: string | null) => {
    setSelectedHouseholdId(value)
    if (onSave) {
      await onSave(value)
    }
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  const placeholderText =
    householdType?.toLowerCase() === "prospect"
      ? "Select prospect..."
      : householdType?.toLowerCase() === "client"
        ? "Select client..."
        : "Select household..."

  return (
    <InlineSelect
      label={label}
      LabelIcon={LabelIcon}
      defaultValue={selectedHousehold?.id ?? null}
      placeholder={isLoading ? "Loading..." : placeholderText}
      options={options}
      disabled={isLoading || isDisabled}
      onSave={handleValueChange}
      onSearch={handleSearch}
      isSearching={isLoading}
      fixedLabelWidth={fixedLabelWidth}
      hideLoader
      hideLabel={hideLabel}
      size={size}
      placeholderContent={placeholderContent}
      renderValue={renderValue}
    />
  )
}
